// State for the "Create Purchase Link" flow launched from the account page,
// built to reuse the same catalog/order-summary UI stack as ProposalSlice
// (see ProposalSlice.js) but keyed by *location* instead of by proposal
// *option* — an account reached from the account page already exists in
// Recurly and may have one or many billable locations, each with its own
// thryvId, country and Recurly subscription state. Only one location is
// "active" (shown in the catalog/order summary) at a time, switched via a
// dropdown — mirrors how ProposalSlice keeps one active option at a time,
// just swapping the axis from option to location.
import {createSlice, createSelector} from '@reduxjs/toolkit'
import {MC_2_0_PLANS} from '../utils/constants'
import {THRYV_BASIC_CODES} from '../utils/tgp-catalog-flags'
import {buildItemsFromAccountSubscriptions} from '../features/CatalogGrid/utils/mapRecurlySubscriptionsToItems'
import {makeProductGroupSelectors} from './shared/productConfigSelectors'

// Per-location default before the rep has touched that location's
// Onboarding Contact — a stable module-level reference (not a fresh literal
// per call) so selectOnboardingContactForLocation's fallback doesn't cause
// spurious re-renders under useSelector's default reference equality.
const DEFAULT_ONBOARDING_CONTACT = {
  useAccountOwner: true,
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
}

const initialAccountInfo = {
  thryvId: null,
  businessName: null,
  email: null,
  firstName: null,
  lastName: null,
  phone: null,
  mobile: null,
  country: null,
  xpCode: null,
  salesRepOrPartner: null,
  billTo: null,
  // Forwarded to buildCreateOrderPayload's company_address1/city/state/zipcode
  // so order_header actually stores a business address — without it, the
  // payment page's "Same as my business address" checkbox has nothing to
  // copy from (see recurly-payment-form.js's setFromBusinessAddress).
  address: null,
  city: null,
  state: null,
  postCode: null,
}

const initialState = {
  // Global plan catalog, fetched once — same shape/source as ProposalSlice's.
  productConfigInfo: {},
  // Tracks whether the active location has unsaved cart changes.
  isDirty: false,
  // Mirrors Enable_BNI_Mktg_Svcs into Redux — buildItemsFromAccountSubscriptions
  // runs inside a plain reducer, which has no access to React context. See
  // ProposalSlice.js's identical field for the full rationale.
  isBniMktgSvcsEnabled: false,
  // The account (parent) this purchase link was opened for.
  accountInfo: {...initialAccountInfo},
  // The logged-in rep creating this purchase link — selectUserData's
  // user?.email / selectEffectiveRole's effectiveRole?.[0], same sourcing
  // ProposalCreationComponent uses for proposalInfo.salesRepId/userRole.
  // Kept at this same level as accountInfo (not nested inside it, and not
  // duplicated per location) since it's an attribute of the rep's session,
  // not of the account/location being ordered for — it's the same value
  // regardless of which location ends up read as `identity`.
  salesRepId: null,
  userRole: null,
  // The order's sales/partner attribution — captured by AccountEditModal's
  // ORDER_FLOW_VISIBLE_FIELD_KEYS fields (the "Create Purchase Link" modal's
  // hideLocationDetails view) as local editValues, then committed here once
  // when the rep clicks Save/Continue (see buildPurchaseLinkState's
  // salesAttributionFields). Kept at this same level as accountInfo, not
  // nested inside it, mirroring salesRepId/userRole above — this describes
  // the order being created, not the account/location identity itself.
  salesAttributionFields: {
    salesRepOrPartner: null,
    xpCode: null,
    overrideSST: null,
    salesRepInfo: null,
    overrideRepInfo: null,
  },
  // One entry per billable location under this account (parent's children).
  // locations: [{
  //   locationId:          string,   // stable id — the location's thryvId
  //   locationName:        string,
  //   thryvId:             string,
  //   country:             string,
  //   billTo:              'self' | 'parent',
  //   accountInfo:         object,   // this location's OWN identity/address
  //                                   // (businessName/email/firstName/lastName/
  //                                   // phone/mobile/country/xpCode/
  //                                   // salesRepOrPartner/address/city/state/
  //                                   // postCode) — set by buildPurchaseLinkState
  //                                   // from the location's own record, distinct
  //                                   // from the parent's `accountInfo` above.
  //                                   // buildSendLinkPayload reads THIS for a
  //                                   // single-location ('location' orderMode)
  //                                   // order instead of the parent's.
  //   isLoaded:            boolean,  // whether recurlyAccountInfo has been fetched
  //   recurlyAccountInfo:  object,
  //   items:               array,    // same item shape as a ProposalSlice option's items[]
  //   coupons:             array,
  //   dueTodayTotal:       number,
  //   monthlyTotal:        number,
  //   onboardingApptId:              string | undefined, // set by SchedulingStep from
  //   thryvSuccessServicesApptId:    string | undefined, // useScheduledAppointments, once booked
  // }]
  locations: [],
  // Which location's catalog/order summary is currently shown.
  selectedLocationId: null,
  // 'location' (default): classic single-location flow — LocationSelector +
  // the single-location OrderSummaryContainer. 'parentPay': the account-header
  // "Create Purchase Link" flow, seeded with only that account's parent-pay
  // children — uses ParentPayLocationSelector + a combined multi-location
  // order summary instead. See AccountRow's hasParentPayChild/onOrderForParent.
  orderMode: 'location',
  // The order_header id returned by ordercapture/services/createOrder, set
  // when the rep clicks Continue on Catalog Selection (status 'Pending',
  // same convention as new-order.js's 'catalog-continue-summary' context) —
  // carried into the Scheduling step as the Lightning Out order-id attribute.
  orderId: null,
  // Whether this journey is "Create Purchase Link" on an existing location
  // that already has a v5 subscription (set from handleOrder/handleSaveAndProceed's
  // accounts/upgradeTarget check in AllAccounts/MyAccounts) vs. a brand-new
  // account/location walking through its first order. NOT the same as "has
  // any active Recurly subscription" — every account, new or not, gets a real
  // $0 baseline subscription (Thryv My Account Basic+) the moment it's
  // created, so that signal can't distinguish new-vs-upgrade on its own. Drives
  // buildCreateOrderPayload's order_flow ('New' vs 'Update').
  isUpgrade: false,
  // "Onboarding Contact" — who the CSM should actually reach out to for the
  // scheduled call, defaulting to the account owner. Ports the old flow's
  // onboarding-program screen's name/email/phone fields + "Same as account
  // owner" checkbox — those DO get sent to the call-booking API as the
  // appointment's contact (see book-appointments.js's confirmOnboard /
  // apptData). Keyed by locationId: Multi-Location Parent Pay books a
  // separate kickoff/TSS call per location, each of which may have its own
  // contact — see SchedulingStep, consumed by useOnboardingScheduling's
  // handleScheduleCall. A location with no entry yet reads as
  // DEFAULT_ONBOARDING_CONTACT (see selectOnboardingContactForLocation).
  onboardingContactByLocationId: {},
}

const purchaseLinkSlice = createSlice({
  name: 'purchaseLink',
  initialState,
  reducers: {
    setAccountInfo(state, action) {
      state.accountInfo = action.payload
    },

    updateAccountInfo(state, action) {
      state.accountInfo = {...state.accountInfo, ...action.payload}
    },

    resetAccountInfo(state) {
      state.accountInfo = {...initialAccountInfo}
    },

    setProductConfigInfo(state, action) {
      state.productConfigInfo = action.payload
    },

    updateProductConfigInfo(state, action) {
      state.productConfigInfo = {...state.productConfigInfo, ...action.payload}
    },

    resetProductConfigInfo(state) {
      state.productConfigInfo = initialState.productConfigInfo
    },

    setBniMktgSvcsEnabled(state, action) {
      state.isBniMktgSvcsEnabled = action.payload
    },

    // Seeds the location list from the account hierarchy the account page
    // already loaded (parent + children) — no extra fetch needed. Each
    // location starts with no Recurly info/items; those are lazily fetched
    // per-location on selection (see usePurchaseLinkLocation).
    initLocations(state, action) {
      const {locations, selectedLocationId, orderMode} = action.payload
      state.locations = locations.map(loc => ({
        isLoaded: false,
        recurlyAccountInfo: {},
        items: [],
        coupons: [],
        dueTodayTotal: 0,
        monthlyTotal: 0,
        ...loc,
      }))
      state.selectedLocationId =
        selectedLocationId ?? state.locations[0]?.locationId ?? null
      state.orderMode = orderMode ?? 'location'
    },

    setSelectedLocationId(state, action) {
      state.selectedLocationId = action.payload
    },

    // Once a location's Recurly account info is loaded, derive its active
    // subscription plans and merge them into that location's items. Mirrors
    // ProposalSlice's setRecurlyAccountInfo merge algorithm exactly, scoped
    // to a single location instead of iterating every proposal option.
    setRecurlyAccountInfoForLocation(state, action) {
      const {locationId, recurlyAccountInfo} = action.payload
      const location = state.locations.find(l => l.locationId === locationId)
      if (!location) return

      location.recurlyAccountInfo = recurlyAccountInfo
      location.isLoaded = true

      const items = buildItemsFromAccountSubscriptions(
        recurlyAccountInfo,
        state.productConfigInfo,
        {isBniMktgSvcsEnabled: state.isBniMktgSvcsEnabled},
      )

      if (!items.length) return

      const recurlyCodes = new Set(items.map(i => i.productCode))

      const {
        centers = [],
        apps = [],
        oneTimeFees = [],
      } = state.productConfigInfo ?? {}
      const uigroupByCode = new Map(
        [...centers, ...apps, ...oneTimeFees].map(p => [p.code, p.uigroup]),
      )

      const existingCodes = new Set(
        (location.items ?? []).map(i => i.productCode),
      )
      const recurlyItemByCode = new Map(items.map(i => [i.productCode, i]))
      location.items = (location.items ?? []).map(item => {
        if (!recurlyCodes.has(item.productCode)) return item
        const recurlyItem = recurlyItemByCode.get(item.productCode)
        return {
          ...item,
          alreadyIncluded: true,
          ...(recurlyItem?.recurlyQuantity != null && {
            recurlyQuantity: recurlyItem.recurlyQuantity,
          }),
        }
      })
      const newItemCodes = (location.items ?? [])
        .filter(i => !recurlyCodes.has(i.productCode))
        .map(i => i.productCode)
      const replacedGroups = new Set(
        newItemCodes.map(code => uigroupByCode.get(code)).filter(Boolean),
      )

      const ALL_MC_CODES = new Set([
        'mc_plus-5-thryv-m2m',
        'mc_pro-5-thryv-m2m',
        'mc_pro_enh-5-thryv-m2m',
        'mc_foundation-5-thryv-m2m',
        ...THRYV_BASIC_CODES,
        ...MC_2_0_PLANS,
      ])
      const hasNewMCPlan = newItemCodes.some(code => ALL_MC_CODES.has(code))

      const newItems = items.filter(i => {
        if (existingCodes.has(i.productCode)) return false
        if (replacedGroups.has(uigroupByCode.get(i.productCode))) return false
        if (hasNewMCPlan && ALL_MC_CODES.has(i.productCode)) return false
        return true
      })
      location.items = [...location.items, ...newItems]
    },

    // Update an existing location's mutable fields (items, coupons,
    // dueTodayTotal, monthlyTotal, ...). Mirrors ProposalSlice's updateOption.
    updateLocation(state, action) {
      const {locationId, changes, skipDirty} = action.payload

      const location = state.locations.find(l => l.locationId === locationId)

      if (location) {
        Object.assign(location, changes)
        if (!skipDirty) state.isDirty = true
      }
    },

    resetDirty(state) {
      state.isDirty = false
    },

    setOrderId(state, action) {
      state.orderId = action.payload
    },

    setIsUpgrade(state, action) {
      state.isUpgrade = action.payload
    },

    setRepInfo(state, action) {
      const {salesRepId, userRole} = action.payload
      state.salesRepId = salesRepId
      state.userRole = userRole
    },

    setSalesAttributionFields(state, action) {
      state.salesAttributionFields = action.payload
    },

    setOnboardingContactUseAccountOwner(state, action) {
      const {locationId, useAccountOwner} = action.payload
      state.onboardingContactByLocationId[locationId] = {
        ...(state.onboardingContactByLocationId[locationId] ??
          DEFAULT_ONBOARDING_CONTACT),
        useAccountOwner,
      }
    },

    setOnboardingContactField(state, action) {
      const {locationId, field, value} = action.payload
      state.onboardingContactByLocationId[locationId] = {
        ...(state.onboardingContactByLocationId[locationId] ??
          DEFAULT_ONBOARDING_CONTACT),
        [field]: value,
      }
    },

    resetAll() {
      return {...initialState}
    },
  },
})

export const {
  setAccountInfo,
  updateAccountInfo,
  resetAccountInfo,
  setProductConfigInfo,
  updateProductConfigInfo,
  resetProductConfigInfo,
  setBniMktgSvcsEnabled,
  initLocations,
  setSelectedLocationId,
  setRecurlyAccountInfoForLocation,
  updateLocation,
  resetDirty,
  setOrderId,
  setIsUpgrade,
  setRepInfo,
  setSalesAttributionFields,
  setOnboardingContactUseAccountOwner,
  setOnboardingContactField,
  resetAll,
} = purchaseLinkSlice.actions

export default purchaseLinkSlice.reducer

/* -------------------------------------------------------------------------- */
/* Selectors */
/* -------------------------------------------------------------------------- */

export const selectOnboardingContactByLocationId = state =>
  state.purchaseLink.onboardingContactByLocationId

export const selectOnboardingContactForLocation = (state, locationId) =>
  state.purchaseLink.onboardingContactByLocationId[locationId] ??
  DEFAULT_ONBOARDING_CONTACT

export const selectProductConfigInfo = state =>
  state.purchaseLink.productConfigInfo

export const {
  selectProductGroups,
  selectSoftwareGroups,
  selectAddonGroups,
  selectIsCatalogDataLoaded,
} = makeProductGroupSelectors(selectProductConfigInfo)

export const selectOneTimeFeeDefinitions = state =>
  state.purchaseLink.productConfigInfo?.oneTimeFees ?? []

export const selectAccountInfo = state => state.purchaseLink.accountInfo

export const selectIsBniMktgSvcsEnabled = state =>
  state.purchaseLink.isBniMktgSvcsEnabled

export const selectLocations = state => state.purchaseLink.locations

export const selectSelectedLocationId = state =>
  state.purchaseLink.selectedLocationId

export const selectOrderMode = state => state.purchaseLink.orderMode

export const selectActiveLocation = createSelector(
  [selectLocations, selectSelectedLocationId],
  (locations, selectedLocationId) =>
    locations.find(l => l.locationId === selectedLocationId) ?? null,
)

export const selectActiveLocationCountry = createSelector(
  [selectActiveLocation],
  location => location?.country ?? null,
)

export const selectActiveLocationRecurlyInfo = createSelector(
  [selectActiveLocation],
  location => location?.recurlyAccountInfo ?? {},
)

export const selectActiveLocationRecurlySubscriptionItems = createSelector(
  [
    selectActiveLocationRecurlyInfo,
    selectProductConfigInfo,
    selectIsBniMktgSvcsEnabled,
  ],
  (recurlyAccountInfo, productConfigInfo, isBniMktgSvcsEnabled) =>
    buildItemsFromAccountSubscriptions(recurlyAccountInfo, productConfigInfo, {
      isBniMktgSvcsEnabled,
    }),
)

export const selectIsDirty = state => state.purchaseLink.isDirty

export const selectOrderId = state => state.purchaseLink.orderId

export const selectIsUpgrade = state => state.purchaseLink.isUpgrade

export const selectSalesRepId = state => state.purchaseLink.salesRepId

export const selectUserRole = state => state.purchaseLink.userRole

export const selectSalesAttributionFields = state =>
  state.purchaseLink.salesAttributionFields

// Gates the Catalog Selection "Continue" button — 'location' mode only cares
// about the single active location; 'parentPay' allows any parent-pay
// location in the combined order to have items (matches Order Summary's own
// combined-total behavior, where an empty location is fine as long as
// another one in the set has something in it).
export const selectHasCartItems = createSelector(
  [selectLocations, selectSelectedLocationId, selectOrderMode],
  (locations, selectedLocationId, orderMode) => {
    if (orderMode === 'parentPay') {
      return locations.some(l => (l.items ?? []).length > 0)
    }
    const active = locations.find(l => l.locationId === selectedLocationId)
    return (active?.items ?? []).length > 0
  },
)
